import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Coins, Check, Sparkles, Palette, Image as ImageIcon, Circle, Zap, Sticker, Smile, Backpack } from "lucide-react";

const CATEGORIES = [
  { key: "background",     label: "Backgrounds", icon: ImageIcon },
  { key: "border",         label: "Borders",     icon: Circle },
  { key: "chat_color",     label: "Chat Colors", icon: Palette },
  { key: "avatar_effect",  label: "Avatar FX",   icon: Zap },
  { key: "sticker",        label: "Stickers",    icon: Sticker },
  { key: "emoticon",       label: "Emoticons",   icon: Smile },
];

const RARITY_COLORS = {
  common:    "#8A8A94",
  rare:      "#00E5FF",
  epic:      "#A78BFA",
  legendary: "#FFD700",
  mythic:    "#FF1053",
};

export default function Shop() {
  const { user, refreshMe } = useAuth();
  const [tab, setTab] = useState("background");
  const [catalog, setCatalog] = useState([]);
  const [inventory, setInventory] = useState({ items: [], loadout: {} });
  const [showInv, setShowInv] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [msg, setMsg] = useState("");

  const load = async () => {
    try { const r = await api.get("/shop/catalog"); setCatalog(r.data || []); } catch { /* */ }
    if (user) {
      try { const inv = await api.get("/shop/inventory"); setInventory(inv.data); } catch { /* */ }
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user?.username]);

  const owned = new Set(inventory.items.map(i => i.id));
  const loadout = inventory.loadout || {};

  const buy = async (item) => {
    if (!user) { setMsg("Log in to buy."); return; }
    if (owned.has(item.id)) { setMsg("Already owned."); return; }
    const sparks = user.sparks ?? user.credits ?? 0;
    if (sparks < item.price) { setMsg("Not enough Sparks."); return; }
    setBusyId(item.id); setMsg("");
    try {
      await api.post("/shop/purchase", { item_id: item.id });
      await refreshMe();
      await load();
      setMsg(`Purchased ${item.name}!`);
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Purchase failed");
    } finally { setBusyId(null); }
  };

  const equip = async (item) => {
    setBusyId(item.id); setMsg("");
    try {
      const cur = loadout[item.category];
      const target = cur === item.id ? null : item.id;
      await api.post("/shop/equip", { item_id: target, category: item.category });
      await load();
      setMsg(target ? `Equipped ${item.name}` : `Unequipped`);
    } catch (e) { setMsg(e?.response?.data?.detail || "Failed"); }
    finally { setBusyId(null); }
  };

  const visible = catalog.filter(i => i.category === tab);

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="sc-label" style={{ color: "var(--sc-orange)" }}>Bling Shop</div>
          <h1 className="sc-heading text-4xl font-black mt-1 flex items-center gap-2"><Sparkles className="w-8 h-8 text-[color:var(--sc-orange)]" /> Deck yourself out</h1>
          <p className="text-sm text-zinc-400 mt-2 max-w-lg">Buy with credits. Backgrounds &amp; borders show on your profile. Chat colors + emote packs come alive everywhere you chat.</p>
        </div>
        <div className="flex items-center gap-2">
          {user && (
            <div className="sparks-pill" data-testid="shop-balance">
              <Sparkles className="w-3.5 h-3.5" /> {user.sparks ?? user.credits ?? 0}
            </div>
          )}
          <button onClick={() => setShowInv(v => !v)} data-testid="shop-inventory-toggle" className="sc-btn-ghost text-xs flex items-center gap-1.5">
            <Backpack className="w-3.5 h-3.5" /> {showInv ? "Hide" : "Inventory"} ({inventory.items.length})
          </button>
        </div>
      </div>

      <div className="text-[11px] sc-mono text-zinc-500 -mt-4 mb-4">Bling is bought with <span className="text-[color:var(--sc-cyan)]">Sparks</span> (free play credits). No gambling — cosmetics only.</div>

      <div className="flex items-center gap-1 flex-wrap mb-6 border-b border-white/5 pb-2 overflow-x-auto">
        {CATEGORIES.map(c => (
          <button key={c.key} onClick={() => setTab(c.key)} data-testid={`shop-tab-${c.key}`} className={`px-3 py-2 rounded-sm text-xs sc-mono tracking-widest uppercase flex items-center gap-1.5 whitespace-nowrap ${tab === c.key ? "bg-white/10 text-white" : "text-zinc-500 hover:text-white"}`}>
            <c.icon className="w-3.5 h-3.5" /> {c.label}
          </button>
        ))}
      </div>

      {msg && <div className="mb-4 text-xs sc-mono text-[color:var(--sc-volt)]" data-testid="shop-msg">{msg}</div>}

      {showInv && user && (
        <div className="sc-card p-5 mb-6" data-testid="shop-inventory-panel">
          <div className="sc-heading text-lg font-bold mb-3">Your Inventory ({inventory.items.length})</div>
          {inventory.items.length === 0 ? (
            <div className="text-xs text-zinc-500">You haven&apos;t bought anything yet.</div>
          ) : (
            <div className="grid sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
              {inventory.items.map(item => (
                <button key={item.id} onClick={() => equip(item)} data-testid={`inv-item-${item.id}`} className={`p-2 rounded-sm border text-left text-[10px] ${loadout[item.category] === item.id ? "border-[color:var(--sc-volt)] bg-[color:var(--sc-volt)]/5" : "border-white/10 hover:border-white/20"}`}>
                  <div className="sc-mono uppercase tracking-widest text-zinc-500 mb-1">{item.category.replace("_", " ")}</div>
                  <ItemPreview item={item} mini />
                  <div className="font-bold mt-1 truncate">{item.name}</div>
                  <div className="text-[9px] mt-0.5" style={{ color: RARITY_COLORS[item.rarity] || "#8A8A94" }}>{loadout[item.category] === item.id ? "EQUIPPED" : "Tap to equip"}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {visible.map(item => {
          const isOwned = owned.has(item.id);
          const isEquipped = loadout[item.category] === item.id;
          const rarityColor = RARITY_COLORS[item.rarity] || "#8A8A94";
          return (
            <div key={item.id} className="sc-card p-4" data-testid={`shop-item-${item.id}`} style={{ borderColor: `${rarityColor}40` }}>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[9px] sc-mono tracking-widest uppercase" style={{ color: rarityColor }}>{item.rarity}{item.animation ? " · animated" : ""}</div>
                <div className="text-[10px] sc-mono flex items-center gap-1"><Sparkles className="w-3 h-3 text-[#00E5FF]" /> {item.price}</div>
              </div>
              <ItemPreview item={item} />
              <div className="font-bold text-sm mt-2 truncate">{item.name}</div>
              <div className="mt-3 flex gap-2">
                {isOwned ? (
                  <button onClick={() => equip(item)} disabled={busyId === item.id} data-testid={`shop-equip-${item.id}`} className={`flex-1 text-xs py-2 rounded-sm font-bold sc-mono tracking-widest uppercase ${isEquipped ? "bg-[color:var(--sc-volt)] text-black" : "border border-white/10 hover:bg-white/5"}`}>
                    {isEquipped ? <><Check className="w-3 h-3 inline mr-1" />Equipped</> : "Equip"}
                  </button>
                ) : (
                  <button onClick={() => buy(item)} disabled={busyId === item.id || !user || (user.sparks ?? user.credits ?? 0) < item.price} data-testid={`shop-buy-${item.id}`} className="sc-btn flex-1 text-xs py-2">
                    {busyId === item.id ? "Buying…" : "Buy"}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ItemPreview({ item, mini = false }) {
  const h = mini ? "h-10" : "h-24";
  const animMap = {
    gradient_shift: "anim-gradient-shift",
    nebula_drift: "anim-nebula-drift",
    starfield: "anim-starfield",
    scanline_scroll: "anim-scanline-scroll",
    pulse: (item.value === "#FF1053" ? "anim-pulse-pink" : "anim-pulse-volt"),
    spin_slow: "anim-spin-slow",
    spin_fast: "anim-spin-fast",
    shimmer_x: "anim-shimmer-x",
    rainbow_glow: "anim-rainbow-glow",
    static_flicker: "anim-static-flicker",
    halo_rotate: "anim-halo",
  };
  const animClass = item.animation ? (animMap[item.animation] || "") : "";
  if (item.category === "background") {
    const baseStyle = item.animation === "starfield" ? {} : { background: item.value };
    return <div className={`w-full ${h} rounded-sm ${animClass}`} style={baseStyle} />;
  }
  if (item.category === "border") {
    const ringStyle = { width: mini ? 32 : 68, height: mini ? 32 : 68, padding: 3 };
    const isSpin = item.animation === "spin_slow" || item.animation === "spin_fast";
    const isShimmer = item.animation === "shimmer_x";
    const bgProp = item.value.startsWith("linear") || item.value.startsWith("radial") || item.value.startsWith("conic") ? { backgroundImage: item.value } : { backgroundColor: item.value };
    return (
      <div className={`w-full ${h} rounded-sm flex items-center justify-center`}>
        <div className={`rounded-full flex items-center justify-center relative ${animClass}`} style={{ ...ringStyle, ...bgProp, transformOrigin: "center", ...(isShimmer ? { backgroundSize: "200% 100%" } : {}) }}>
          <div className="rounded-full w-full h-full bg-[#0A0A0C] flex items-center justify-center text-[9px] sc-mono text-zinc-500" style={{ animation: isSpin ? "spin_slow_kf 6s linear infinite reverse" : undefined }}>AV</div>
        </div>
      </div>
    );
  }
  if (item.category === "chat_color") {
    const isGrad = item.value.startsWith("linear") || item.value.startsWith("radial") || item.value.startsWith("conic");
    const style = isGrad ? { background: item.value, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", color: "transparent" } : { color: item.value };
    return <div className={`w-full ${h} rounded-sm flex items-center justify-center font-black text-lg ${isGrad && item.animation === "gradient_shift" ? "anim-gradient-shift" : ""}`} style={{ background: "rgba(255,255,255,0.03)" }}><span style={style}>USERNAME</span></div>;
  }
  if (item.category === "avatar_effect") {
    const isBoxShadow = typeof item.value === "string" && item.value.startsWith("0 0");
    const ringStyle = { width: mini ? 32 : 56, height: mini ? 32 : 56 };
    return (
      <div className={`w-full ${h} rounded-sm flex items-center justify-center relative`}>
        <div className={`rounded-full ${animClass} relative`} style={{ ...ringStyle, background: "linear-gradient(135deg,#FF1053,#FF5900)", boxShadow: isBoxShadow ? item.value : undefined }} />
      </div>
    );
  }
  if (item.category === "sticker" || item.category === "emoticon") {
    const parts = (item.value || "").split(",").map(p => p.trim()).filter(Boolean);
    const first = parts.slice(0, mini ? 4 : 8);
    return (
      <div className={`w-full ${h} rounded-sm flex flex-wrap items-center justify-center gap-1 p-1`} style={{ background: "rgba(255,255,255,0.03)" }}>
        {first.map((s, i) => {
          const emoji = s.includes("=") ? s.split("=")[1] : s;
          return <span key={i} className={mini ? "text-base" : "text-2xl"}>{emoji}</span>;
        })}
      </div>
    );
  }
  return null;
}
